import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, NgModel } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  FormGroupComponent,
  InputPasswordComponent,
  MbsPopupType,
  MbsSize,
  RadioComponent,
  RadioDescription,
  SelectComponent
} from 'mbs-ui-kit';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { Administrator, StorageType } from 'mbs-ui-shared@models';
import { GetBackupContentBunchType, ItemTypeEnum } from 'mbs-ui-shared@models/backup/storages-type';
import Computer, { OsType } from 'mbs-ui-shared@models/Computer';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { ProviderService } from 'mbs-ui-shared@services/provider.service';
import { baseDateFormatWithSeconds, cobbledDateFormat, dateFormatWithSpaceAndSeconds } from 'mbs-ui-shared@utils/date';
import { storageAccountsToDestinationsArray } from 'mbs-ui-shared@utils/storage-accounts-to-destinations-array';
import moment from 'moment/moment';
import { combineLatest, filter, first, Observable, of, switchMap, tap } from 'rxjs';
import { take } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { cbfMap, IdsForCurrentFormats } from 'wizards/models/backup-to-restore-models';
import { FromToStepData, RestoreToVariants, WhatToStepData } from 'wizards/models/generic-restore/generic-restore-models';
import { RestorePointItem, RestoreType } from 'wizards/models/restore-point-models';

import { I18NextPipe } from 'angular-i18next';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { PasswordRecoveryHandlerComponent } from 'mbs-ui-shared@modules/password-recovery/components/password-recovery-handler/password-recovery-handler.component';
import {
  GetBackupContentParams,
  ParamsForRCRestoreMethods,
  PlanForRestore,
  PrefixForRestore,
  RequestForRestore,
  WizardStepsService
} from 'wizards/services/wizard-steps.service';

@UntilDestroy()
@Component({
  selector: 'mbs-generic-what-to-restore-step',
  templateUrl: './generic-what-to-restore-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ButtonComponent,
    CardComponent,
    CardGroupComponent,
    FormGroupComponent,
    FormsModule,
    I18NextPipe,
    InputPasswordComponent,
    NgbTooltipModule,
    RadioComponent,
    RadioDescription,
    SelectComponent,
    PasswordRecoveryHandlerComponent
  ]
})
export class GenericWhatToRestoreStepComponent {
  private auth = inject(AuthFacade);
  private facade = inject(ComputerBackupFacade);
  private injector = inject(Injector);
  private providerService = inject(ProviderService);
  private stepService = inject(WizardStepsService);
  private i18Next = inject(I18NextPipe);

  destinationSelect = viewChild<NgModel>('destinationSelect');
  planSelect = viewChild<NgModel>('planSelect');
  pointSelect = viewChild<NgModel>('pointSelect');

  public readonly size = MbsSize;
  public readonly mbsPopupType = MbsPopupType;
  public readonly restoreType = RestoreType;
  public readonly restoreToVariants = RestoreToVariants;
  public readonly backupVersionUpdated = '800';

  public readonly elementsSelector = {
    name: {
      passwordBlock: 'passwordBlock',
      passwordHintBlock: 'passwordHintBlock',
      passwordHintText: 'passwordHintText'
    }
  };
  private readonly sqlTypes: string[] = [GetBackupContentBunchType.MsSql, GetBackupContentBunchType.Database];
  private readonly fileCbf: string[] = [IdsForCurrentFormats.File, IdsForCurrentFormats.FileUnix];

  public fromToStepData: InputSignal<FromToStepData> = input<FromToStepData>();

  public whatToStepData: OutputEmitterRef<WhatToStepData> = output<WhatToStepData>();

  public loadingDestinations: WritableSignal<boolean> = signal(true);
  public loadingPlans: WritableSignal<boolean> = signal(true);
  public loadingPoints: WritableSignal<boolean> = signal(true);

  public selectedDestination: WritableSignal<StorageConnection> = signal(null);
  public destinationItemsFullList: StorageConnection[] = [];
  public destinationItems: StorageConnection[] = [];

  public selectedPlan: WritableSignal<PlanForRestore> = signal(null);
  public planItems: PlanForRestore[] = [];

  public restorePointType: WritableSignal<RestoreType> = signal(RestoreType.LatestVersion);
  public selectedPoint: WritableSignal<RestorePointItem> = signal(null);
  public existFiltersPoints: WritableSignal<boolean> = signal(false);
  public pointItems: RestorePointItem[] = [];

  public password: WritableSignal<string> = signal('');
  public showHint = false;
  public allowedRecovery = false;
  public passwordControlFacade = new FormControl('');

  public loading: Signal<boolean> = computed(() => this.loadingDestinations() || this.loadingPlans() || this.loadingPoints());

  public isUnix = false;
  public isProvider: boolean;
  public passwordRecoveryEnabled: boolean;
  public providerEmail: string;
  public recoveryDate: string;

  public showRecoveryInfo: Signal<boolean> = computed(() => {
    const point = this.selectedPoint();

    if (!point || this.isProvider || !this.passwordRecoveryEnabled || !this.recoveryDate) return false;

    return moment(point.date, baseDateFormatWithSeconds).isAfter(moment(this.recoveryDate, dateFormatWithSpaceAndSeconds));
  });

  public valid: Signal<boolean> = computed(
    () =>
      !this.loading() &&
      !!this.selectedDestination() &&
      !!this.selectedPlan() &&
      (this.restorePointType() === RestoreType.LatestVersion || !!this.selectedPoint())
  );

  private loadedForHid: string;
  private lastRestoreTo: RestoreToVariants;

  constructor() {
    this.initSubscribes();
  }

  public backupDestinationChangeHandler(): void {
    this.loadPlans();
  }

  public backupPlanChangeHandler(): void {
    this.loadPoints();
  }

  public backupPointChangeHandler(): void {
    this.emitWhatToStepData();
  }

  public passwordInputHandler(event: Event): void {
    const value = (event?.target as EventTarget & { value: string })?.value;
    this.password.set(value);
    this.passwordControlFacade.setValue(value, { emitEvent: false });
    this.emitWhatToStepData();
  }

  public showPasswordHintChangeHandler(): void {
    this.showHint = !this.showHint;
  }

  private initSubscribes(): void {
    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user: Administrator) => {
      this.isProvider = user.IsProvider;
      this.passwordRecoveryEnabled = user.ProviderInfo?.PasswordRecoveryEnabled;
      this.allowedRecovery = this.passwordRecoveryEnabled && this.isProvider;
      this.providerEmail = user.BrandingSetting?.SupportEmail || user.ProviderInfo?.EmailNotificationsTo;

      if (!this.isProvider && this.passwordRecoveryEnabled) this.getPassRecoveryInfo();
    });

    toObservable(this.fromToStepData)
      .pipe(
        tap((data: FromToStepData) => {
          const comp = data?.fromComputer;
          if (this.loadedForHid && !comp) this.loadedForHid = null;
          if (comp && comp.hid === this.loadedForHid && this.destinationItemsFullList?.length && data?.restoreTo !== this.lastRestoreTo) {
            this.resetDestinationsAfterSomeChanged(data.restoreTo);
          }
          this.lastRestoreTo = data?.restoreTo;
        }),
        filter((data: FromToStepData) => !!data?.fromComputer?.hid && data.fromComputer.hid !== this.loadedForHid),
        tap((data: FromToStepData) => {
          this.isUnix = Computer.isUnix(data.fromComputer);
          this.resetAllData();
          this.facade.loadDestinations({ hid: data.fromComputer.hid, force: true });
        }),
        switchMap((data: FromToStepData) => {
          const hid = data?.fromComputer?.hid;
          const destinationsReq$ = toObservable(this.facade.destinationsByHid(hid), { injector: this.injector });
          const destLoadingReq$ = toObservable(this.facade.destinationsByHidLoading(hid), { injector: this.injector });
          return combineLatest([destinationsReq$, destLoadingReq$]).pipe(
            filter(([destinations, loading]) => !loading),
            first()
          );
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: ([destinations, loading]) => {
          const fromToStepData = this.fromToStepData();
          const computer = fromToStepData.fromComputer;
          this.loadedForHid = computer.hid;
          this.destinationItemsFullList = storageAccountsToDestinationsArray(destinations || []);
          this.updateDestinationListAndSetData(this.destinationItemsFullList, fromToStepData.restoreTo);
        },
        error: () => {
          this.destinationItems = [];
          this.destinationItemsFullList = [];
          this.selectedDestination.set(null);
          this.forceValidState(this.destinationSelect());

          this.offAllLoadings();
        }
      });

    toObservable(this.loading)
      .pipe(
        filter((loading) => !loading),
        untilDestroyed(this)
      )
      .subscribe({ next: () => this.emitWhatToStepData() });

    this.passwordControlFacade.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value !== this.password()) {
        this.password.set(value || '');
        this.emitWhatToStepData();
      }
    });
  }

  private resetDestinationsAfterSomeChanged(restoreTo: RestoreToVariants): void {
    this.destinationItems =
      restoreTo === RestoreToVariants.different
        ? this.destinationItemsFullList.filter((d) => d.StorageType !== StorageType.LocalFS || !d.IsLocal)
        : this.destinationItemsFullList;
    this.selectedDestination.set(this.destinationItems[0]);
  }

  private updateDestinationListAndSetData(myDestinations: StorageConnection[], restoreTo: RestoreToVariants): void {
    this.destinationItems =
      restoreTo === RestoreToVariants.different
        ? myDestinations.filter((d) => d.StorageType !== StorageType.LocalFS || !d.IsLocal)
        : myDestinations;
    this.selectedDestination.set(this.destinationItems[0]);

    if (!this.destinationItems?.length) {
      this.forceValidState(this.destinationSelect());
      this.forceValidState(this.planSelect());
    }

    if (this.selectedDestination()) this.loadPlans();
    else return this.offAllLoadings();

    this.loadingDestinations.set(false);
  }

  private loadPlans(): void {
    this.resetPlansData();

    const fromTo: FromToStepData = this.fromToStepData();
    const computer: Computer = fromTo.fromComputer;
    const isWindows = Computer.isWindows(computer);

    this.stepService
      .getRemoteCommandData(this.getParamsForPlans(), computer.hid)
      .pipe(
        first(),
        switchMap((requestData: RequestForRestore) => this.getNextRequestForPlans(requestData, computer)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (plansRequest: RequestForRestore) => {
          const isCurrent = fromTo.restoreTo === RestoreToVariants.current;
          const isDiff = fromTo.restoreTo === RestoreToVariants.different;
          this.planItems = ((plansRequest?.data?.items || []) as PlanForRestore[])
            .filter((p) => p.type !== GetBackupContentBunchType.Exchange && (!isCurrent || !this.sqlTypes.includes(p.type)))
            .map((p) => this.getUpdatedPlans(p, isWindows))
            .filter((el) => !isDiff || ((el.isNBF || this.fileCbf.includes(el.displayName)) && !this.sqlTypes.includes(el.type)))
            .sort((el) => (el.isNBF ? -1 : 1));
          this.selectedPlan.set(this.planItems[0]);

          if (!this.planItems?.length) this.forceValidState(this.planSelect());

          if (this.selectedPlan()) this.loadPoints();
          else this.resetPointsData(false);

          this.loadingPlans.set(false);
        },
        error: () => {
          this.loadingPlans.set(false);
          this.resetPointsData(false);
          this.forceValidState(this.planSelect());
        }
      });
  }

  private loadPoints(): void {
    this.resetPointsData();

    this.stepService
      .getRemoteCommandData(this.getParamsForPoints(this.selectedPlan().path), this.fromToStepData().fromComputer.hid)
      .pipe(first())
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (pointsRequest: { data: RestorePointItem[] }) => {
          this.pointItems = (pointsRequest?.data || [])
            .filter((point) => point.isContainNewData)
            .sort((a, b) => (moment(a.restorePointId, cobbledDateFormat).isAfter(moment(b.restorePointId, cobbledDateFormat)) ? -1 : 1))
            .map((point) => ({
              ...point,
              label: StepsHelpers.getPointLabel(point)
            }));
          this.selectedPoint.set(this.pointItems[0]);

          if (!this.pointItems?.length) this.forceValidState(this.pointSelect());

          this.existFiltersPoints.set(!this.pointItems?.length && !!pointsRequest?.data?.length);
          this.loadingPoints.set(false);
        },
        error: () => {
          this.resetPointsData(false);
          this.forceValidState(this.pointSelect());
        }
      });
  }

  private getNextRequestForPlans(requestData: RequestForRestore, computer: Computer): Observable<RequestForRestore> {
    if (computer.online && requestData?.data?.items?.some((b) => b.itemType === ItemTypeEnum.Bunch)) {
      return of(requestData);
    }
    if (!requestData?.data?.items?.length) return of({ data: { items: [] } });

    const items = requestData.data.items as PrefixForRestore[];

    let current: PrefixForRestore = items.find((el) => el.isCurrent);

    if (!current) current = items.find((el) => el.displayName === computer.name || el.name === computer.name);
    if (!current) current = items[0];

    return this.stepService
      .getRemoteCommandData(this.getParamsForPlans(current.path), this.fromToStepData().fromComputer.hid)
      .pipe(first());
  }

  private getParamsForPlans(path?: string): GetBackupContentParams {
    return {
      agentType: 'backup',
      commandType: 'GetBackupContent',
      params: {
        SessionId: null,
        ConnectionId: null,
        Password: '',
        ContentFilter: 'Actual',
        path: path || this.selectedDestination().ID,
        offset: 0,
        limit: 300,
        order: `TypeAsc`
      }
    };
  }

  private getParamsForPoints(path: string): ParamsForRCRestoreMethods {
    const pathArray = path.split(this.fromToStepData()?.fromComputer?.os === OsType.windows ? '\\' : '/');

    return {
      agentType: 'backup',
      commandType: 'GetRestorePointList',
      params: {
        ConnectionId: this.selectedDestination()?.ID,
        BunchId: this.selectedPlan()?.id || this.selectedPlan()?.bunchId || pathArray[2] || '',
        RestoreSourcePrefix: pathArray[1],
        GenerationId: null
      }
    };
  }

  private getUpdatedPlans(plan: PlanForRestore, isWindows: boolean): PlanForRestore {
    const isNBF = !cbfMap[plan.id] && !cbfMap[plan.displayName] && plan.displayName !== IdsForCurrentFormats.FileUnix;

    if (!isNBF) {
      return isWindows
        ? { ...plan, displayName: plan.displayName + ` (${this.i18Next.transform('wizards:legacyTag')})` }
        : { ...plan, displayName: plan.displayName + ` (${this.i18Next.transform('wizards:legacyTag')})`, isNBF };
    }

    return isWindows ? plan : { ...plan, isNBF };
  }

  private emitWhatToStepData(): void {
    this.whatToStepData.emit({
      destination: this.selectedDestination(),
      plan: this.selectedPlan(),
      point: this.selectedPoint(),
      pointType: this.restorePointType(),
      password: this.password()
    });
  }

  private resetAllData(): void {
    this.resetDestinationsData();
    this.resetPlansData();
    this.resetPointsData();
  }

  private offAllLoadings(): void {
    this.loadingDestinations.set(false);
    this.resetPlansData(false);
    this.resetPointsData(false);
  }

  private resetDestinationsData(): void {
    this.loadingDestinations.set(true);
    this.destinationItems = [];
    this.selectedDestination.set(null);
  }

  private resetPlansData(state = true): void {
    this.loadingPlans.set(state);
    this.planItems = [];
    this.selectedPlan.set(null);
  }

  private resetPointsData(state = true): void {
    this.loadingPoints.set(state);
    this.restorePointType.set(RestoreType.LatestVersion);
    this.pointItems = [];
    this.selectedPoint.set(null);
  }

  private forceValidState(ngModel: NgModel): void {
    if (!ngModel) return;

    ngModel.control.markAsDirty();
    ngModel.control.markAsTouched();
    ngModel.control.updateValueAndValidity();
  }

  private getPassRecoveryInfo(): void {
    this.providerService
      .getPasswordRecoveryDate()
      .pipe(take(1))
      .subscribe({ next: (result: { keyDate: string }) => (this.recoveryDate = result.keyDate) });
  }
}
